import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, ClickAwayListener, InputBase, Paper, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { NumericFormat } from 'react-number-format';
import { useCellAnnotationStore } from 'store/useCellAnnotationStore';
import { worldCurrencies } from 'utilities';
import MoneyChip from './components/MoneyChip';
import { fetchPreFxPreview } from './services/preFxPreview';
import type {
  CellAnnotationCellKey,
  CellAnnotationConfig,
  CellAnnotationRowState,
  CellAnnotationState,
  PreFxAmountSign,
} from './types';

// Pre-FX single-cell popout: a lone scalar-spreadsheet-shaped cell where the
// value viewer holds both the currency chip and the amount input. Clicking
// the chip opens a currency picker; typing into the input edits the source
// amount. The popout owns its own draft state - only flushes to the
// cell-annotation store on close, so an open-then-close-without-edits is a
// true no-op (no spurious dirty flags).
//
// Wire path: on close-with-edits we call `setRows(cellKey, [{position: 0,
// value: <number>, metadata: {source_currency: <code>}}])`. The host parent's
// save flow folds this into the parent's `cell_annotations` map under the
// host field alias (e.g. `cost_basis`/`amount`/`proceeds`) keyed by
// `pre_fx_currency`, alongside any other annotation types.

// Sized to mirror the host cell's width exactly (see `useLayoutEffect` below)
// so the popout reads as an inline editor for that cell rather than a floating
// dialog that overflows the column.
const PopoutPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.5, 1),
  border: `1px solid ${(theme.palette as any).blue?.primary ?? '#1976d2'}`,
  borderRadius: 2,
  boxSizing: 'border-box',
}));

const AmountInput = styled(InputBase)({
  flex: 1,
  minWidth: 0,
  fontSize: 13,
  '& input': {
    padding: 0,
    textAlign: 'right',
  },
});

// Truncate a host-currency amount to at most 2 decimal places. The host
// cells on the fund-ownership shares ledger validate against >2-decimal
// values (e.g. cost basis), so a converted amount like 12345.6789 would
// otherwise fail validation the moment we stamp it into the cell. We
// truncate (not round) because the Pre-FX annotation is the authoritative
// source; the host cell is presentational and rounding could drift it
// away from the source on a repeated open/close. Source amount is NOT
// truncated, it stays exactly as the user typed it.
//
// Implementation note: a naive `Math.trunc(value * 100) / 100` corrupts
// monetary values because `0.29 * 100 === 28.999999999999996` in IEEE 754,
// which truncates to 28 -> 0.28. Go through `toFixed` first so the
// binary->decimal conversion lands at the user-visible representation, then
// slice off everything past the second decimal place (truncation toward zero).
const truncToTwoDecimals = (value: number): number => {
  if (!Number.isFinite(value)) {
    return value;
  }
  // 10 fractional digits is more than enough headroom to surface the user's
  // intended decimal without re-introducing binary float artifacts at the
  // boundary we care about (2 places). For a value like 0.29 this gives
  // "0.2900000000" which slices to "0.29" — the original 0.29.
  const fixed = value.toFixed(10);
  const dot = fixed.indexOf('.');
  if (dot === -1) {
    return Number(fixed);
  }
  return Number(fixed.slice(0, dot + 3));
};

// Coerce a cell-resident date string to ISO YYYY-MM-DD. Spreadsheet date
// cells often show MM/DD/YYYY in the user-visible value; the backend
// preview endpoint wants ISO. Empty/null/whitespace -> undefined so the
// effect bails cleanly rather than calling the backend with junk.
const normalizeIsoDate = (raw: string | null | undefined): string | undefined => {
  if (raw === null || raw === undefined) {
    return undefined;
  }
  const trimmed = String(raw).trim();
  if (trimmed === '') {
    return undefined;
  }
  // Already ISO: YYYY-MM-DD prefix.
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }
  // Slashed: M/D/YYYY or MM/DD/YYYY.
  const slashed = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashed) {
    const [, m, d, y] = slashed;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return undefined;
};

// Treat blank / non-numeric input as "user provided no amount" so an
// open-then-close without edits is a no-op. The input now stores the raw
// numeric string (no thousand separators / no prefix) thanks to
// `valueIsNumericString` on NumericFormat, so `Number(...)` parses cleanly.
const parseAmount = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return null;
  }
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
};

const PRE_FX_CONFIG: CellAnnotationConfig = {
  annotationType: 'pre_fx_currency',
  rowMode: 'single_cell',
  columns: [],
};

// Input text for a stored source amount, shown in the host's sign when it has one.
const toDraftAmount = (row: CellAnnotationRowState | undefined, amountSign?: PreFxAmountSign): string => {
  if (row?.value === null || row?.value === undefined) {
    return '';
  }
  return String(amountSign ? amountSign.toDisplay(Number(row.value)) : row.value);
};

export interface PreFxPopoutSpreadsheetProps {
  // Whether the popout is currently open. The parent owns the open/close state
  // so the same anchor cell can drive both the bar/icon and the popout.
  open: boolean;
  // Anchor for the Popper - the cell viewer container, NOT the icon. Anchoring
  // on the cell means `placement="bottom-start"` aligns the popout to the
  // cell's left edge (fixes the "drifts to the right" regression).
  anchorEl: HTMLElement | null;
  // Cell key under which Pre-FX state is stored
  // (`<parentType>:<parentId>:<fieldAlias>:pre_fx_currency`).
  cellKey: CellAnnotationCellKey;
  // Default source currency to pre-fill when the user has not yet selected
  // one - typically the parent's host currency
  // (cap_table.captable_currency for FOD, cap_table.company.irr_currency
  // for CFL).
  defaultCurrency: string;
  // Host currency for the live FX preview (cap-table currency for FOD,
  // company IRR currency for CFL). When omitted, the preview is disabled —
  // the popout still edits the annotation, but the host cell isn't touched.
  hostCurrency?: string;
  // Date used to look up the FX rate for the preview. ISO YYYY-MM-DD.
  // Pulled from the parent row's transaction date by the host wiring.
  // Accepts a function so callers can read the date dynamically from the
  // live cell map at each preview attempt instead of capturing a static
  // (often stale) snapshot at parse-cells time.
  transactionDate?: string | (() => string | undefined | null);
  // Receives the converted host amount each time the live preview resolves.
  // The parent uses this to patch its rows state so the host cell shows the
  // converted value immediately (and FOD validation passes before save).
  onHostPreview?: (value: number | null) => void;
  // Optional host sign rule: the input shows `toDisplay` of the stored amount and saves `toStored` of what was typed.
  amountSign?: PreFxAmountSign;
  // Called when the popout should close (Escape or click-outside). Edits
  // have already been flushed to the store by the time this fires.
  onClose: () => void;
}

const PreFxPopoutSpreadsheet: React.FC<PreFxPopoutSpreadsheetProps> = ({
  open,
  anchorEl,
  cellKey,
  defaultCurrency,
  hostCurrency,
  transactionDate,
  onHostPreview,
  amountSign,
  onClose,
}) => {
  // Read the initial draft once when the popout opens so subsequent store
  // updates (e.g. from another popout instance) don't trample local edits.
  const initialState = useCellAnnotationStore.getState().states[cellKey];
  const initialRow: CellAnnotationRowState | undefined = initialState?.rows.find(r => !r.isPlaceholder);

  const [sourceCurrency, setSourceCurrency] = useState<string>(
    () => (initialRow?.metadata?.source_currency as string | undefined) ?? defaultCurrency
  );
  const [sourceAmount, setSourceAmount] = useState<string>(() => toDraftAmount(initialRow, amountSign));

  // The draft the popout opened with. Edits are detected against this, in the same display
  // space the user types in, so opening and closing without typing is always a no-op.
  const openedWithRef = useRef({ amount: sourceAmount, currency: sourceCurrency });

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const fresh = useCellAnnotationStore.getState().states[cellKey];
      const row = fresh?.rows.find(r => !r.isPlaceholder);
      const currency = (row?.metadata?.source_currency as string | undefined) ?? defaultCurrency;
      const amount = toDraftAmount(row, amountSign);
      setSourceCurrency(currency);
      setSourceAmount(amount);
      openedWithRef.current = { amount, currency };
    }
    wasOpenRef.current = open;
  }, [open, cellKey, defaultCurrency, amountSign]);

  // Mirror the host cell's width so the popout sits inside the column instead
  // of overflowing it.
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (open && anchorEl) {
      setAnchorWidth(anchorEl.getBoundingClientRect().width);
    }
  }, [open, anchorEl]);

  const firePreview = useCallback(async () => {
    if (!onHostPreview || !hostCurrency) {
      return;
    }
    const rawDate = typeof transactionDate === 'function' ? transactionDate() : transactionDate;
    const resolvedDate = normalizeIsoDate(rawDate);
    if (!resolvedDate) {
      return;
    }
    const parsed = parseAmount(sourceAmount);
    if (parsed === null) {
      return;
    }
    if (sourceCurrency === hostCurrency) {
      // Identity: avoid the round-trip and report immediately.
      onHostPreview(truncToTwoDecimals(parsed));
      return;
    }
    try {
      const { hostAmount } = await fetchPreFxPreview({
        sourceAmount: parsed,
        sourceCurrency,
        hostCurrency,
        transactionDate: resolvedDate,
      });
      onHostPreview(truncToTwoDecimals(hostAmount));
    } catch (err) {
      // Save re-runs the conversion authoritatively and surfaces the real
      // error there if the rate stays unavailable, so we don't block the
      // popout on preview failure. We log in dev so programming bugs,
      // auth failures, and CapIQ timeouts aren't completely invisible.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[PreFx] fetchPreFxPreview failed:', err);
      }
    }
  }, [sourceAmount, sourceCurrency, hostCurrency, transactionDate, onHostPreview]);

  const flushAndClose = useCallback(() => {
    const openedWith = openedWithRef.current;
    if (sourceAmount.trim() === openedWith.amount.trim() && sourceCurrency === openedWith.currency) {
      onClose();
      return;
    }

    const typed = parseAmount(sourceAmount);
    const parsed = typed !== null && amountSign ? amountSign.toStored(typed) : typed;
    const store = useCellAnnotationStore.getState();
    const existing: CellAnnotationState | undefined = store.states[cellKey];
    const existingRow = existing?.rows.find(r => !r.isPlaceholder);
    const existingValue = existingRow?.value ?? null;
    const existingCurrency = (existingRow?.metadata?.source_currency as string | undefined) ?? null;

    const noLocalAmount = parsed === null;
    const noExistingState = !existing || (existingValue === null && existingCurrency === null);
    if (noLocalAmount && noExistingState) {
      onClose();
      return;
    }

    if (parsed === existingValue && sourceCurrency === existingCurrency) {
      onClose();
      return;
    }

    store.seedIfMissing(cellKey, PRE_FX_CONFIG);
    store.setRows(cellKey, [
      {
        position: 0,
        value: parsed,
        metadata: { source_currency: sourceCurrency },
        isPlaceholder: false,
      },
    ]);
    // Update the host cell to the converted value. Without this, clicking
    // away or pressing Escape persists the Pre-FX annotation but leaves the
    // host amount stale — breaking validation on fresh FOD rows where the
    // host column must be populated for the spreadsheet save, and producing
    // a desync between the source annotation and the displayed host. We
    // fire-and-forget here (matching the Enter path); the save flow re-runs
    // the conversion authoritatively if the preview fetch fails.
    firePreview().catch(err => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[PreFx] firePreview rejected on close:', err);
      }
    });
    onClose();
  }, [sourceAmount, sourceCurrency, cellKey, onClose, firePreview, amountSign]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Stop propagation so the host data-grid doesn't intercept arrows /
      // Backspace and break native input behavior - same pattern as
      // CellAnnotationPopupSheet. Both Enter and Escape route through
      // `flushAndClose`, which is now responsible for triggering the host
      // preview update when the value/currency actually changed.
      event.stopPropagation();
      if (event.key === 'Escape' || event.key === 'Enter') {
        flushAndClose();
      }
    },
    [flushAndClose]
  );

  if (!open) {
    return null;
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      sx={{ zIndex: theme => theme.zIndex.modal }}
      // Match the host cell's left edge exactly.
      modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}>
      <ClickAwayListener onClickAway={flushAndClose}>
        <Box onKeyDown={handleKeyDown} onKeyUp={event => event.stopPropagation()}>
          <PopoutPaper
            elevation={4}
            data-testid={`pre-fx-popout-${cellKey}`}
            style={anchorWidth ? { width: anchorWidth } : undefined}>
            <MoneyChip
              value={sourceCurrency}
              onChange={setSourceCurrency}
              tooltip="Source currency"
              testId={`pre-fx-currency-${cellKey}`}
            />
            <NumericFormat
              customInput={AmountInput}
              autoFocus
              value={sourceAmount}
              valueIsNumericString
              thousandSeparator
              allowNegative
              // No `decimalScale`: the host cell decides display precision; the
              // editor must accept whatever the user types so a Pre-FX amount
              // can carry full precision through to FX conversion.
              prefix={`${worldCurrencies[sourceCurrency as keyof typeof worldCurrencies] ?? ''} `}
              onValueChange={values => setSourceAmount(values.value)}
              inputProps={{
                inputMode: 'decimal',
                'aria-label': 'Source amount',
                'data-testid': `pre-fx-amount-${cellKey}`,
              }}
              placeholder="0.00"
            />
          </PopoutPaper>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};

export default PreFxPopoutSpreadsheet;
